import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { createBRS } from '../../../../redux/actions/SocietyPortal/BRSAction/BRSAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';

const inputClass = 'tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500';
const labelClass = 'tw-block tw-text-[11px] tw-font-medium tw-text-gray-700 tw-mb-1';

const fyStartEnd = (fy) => {
    // fy like "2026-2027" => start 2026-04-01, end 2027-03-31
    const [a, b] = (fy || '').split('-');
    if (!a || !b) return { start: '', end: '' };
    return { start: `${a}-04-01`, end: `${b}-03-31` };
};

const BRSCreateModal = ({ financialYear, bankAccounts, onClose, onSaved }) => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const { createLoading } = useSelector(state => state.BRSReducer || {});

    const { start, end } = fyStartEnd(financialYear);

    const [accountCd, setAccountCd] = useState('');
    const [startDate, setStartDate] = useState(start);
    const [endDate, setEndDate] = useState(end);
    const [bankBalance, setBankBalance] = useState('');

    const bankAccountOptions = useMemo(() => {
        return (bankAccounts || []).map(acc => ({
            value: acc.gl_code,
            label: `${acc.name} (${acc.gl_code})`,
        }));
    }, [bankAccounts]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!accountCd) { toast.warn('Select a bank account'); return; }
        if (!startDate || !endDate) { toast.warn('Start and end dates are required'); return; }
        if (new Date(endDate) <= new Date(startDate)) { toast.warn('End date must be after start date'); return; }
        if (bankBalance === '') { toast.warn('Bank balance is required'); return; }

        dispatch(createBRS({
            societyId,
            account_cd: accountCd,
            start_date: startDate,
            end_date: endDate,
            fin_year: financialYear,
            bank_balance: parseFloat(bankBalance),
            onSuccess: () => onSaved?.(),
        }));
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40" onClick={onClose}>
            <div
                className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-md tw-mx-4 tw-overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-2.5">
                        <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-gradient-to-br tw-from-rose-500 tw-to-pink-600 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="hugeicons:structure-03" className="tw-text-white tw-text-base" />
                        </div>
                        <div>
                            <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">Create New BRS</h2>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Reconcile book vs bank for a period</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="tw-p-5 tw-space-y-3">
                        <div>
                            <label className={labelClass}>Bank Account <span className="tw-text-red-500">*</span></label>
                            <SearchableDropdown
                                value={accountCd || 'all'}
                                onChange={(v) => setAccountCd(v === 'all' ? '' : v)}
                                options={bankAccountOptions}
                                placeholder="Select bank account"
                                small
                            />
                        </div>
                        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                            <div>
                                <label className={labelClass}>Start Date <span className="tw-text-red-500">*</span></label>
                                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputClass} required />
                                <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">FY starts {start}</p>
                            </div>
                            <div>
                                <label className={labelClass}>End Date <span className="tw-text-red-500">*</span></label>
                                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputClass} required />
                                <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">FY ends {end}</p>
                            </div>
                        </div>
                        <div>
                            <label className={labelClass}>Bank Balance (from physical statement) <span className="tw-text-red-500">*</span></label>
                            <input
                                type="number"
                                step="0.01"
                                value={bankBalance}
                                onChange={(e) => setBankBalance(e.target.value)}
                                placeholder="0.00"
                                className={inputClass}
                                required
                            />
                        </div>
                    </div>

                    <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-bg-gray-50">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={createLoading}
                            className="tw-px-4 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={createLoading}
                            className="tw-flex tw-items-center tw-gap-1 tw-px-4 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        >
                            {createLoading
                                ? <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent"></div>
                                : <Icon icon="mdi:plus" className="tw-text-sm" />}
                            Create BRS
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BRSCreateModal;
